<template>
  <section
    class="bp-order"
    data-bp-station="contact"
    aria-labelledby="contact-me"
  >
    <div class="bp-order__strip">
      <span class="bp-label">Contact / work order</span>
      <span class="bp-label">Port 4</span>
    </div>
    <div class="bp-order__body">
      <h2
        id="contact-me"
        class="bp-order__title"
      >
        Contact Me
      </h2>
      <p class="bp-order__intro">
        <strong>Have any questions?</strong> If you need consulting work, are interested in employing me for A/V or
        other projects, or just want to say hi, I'd love to hear from you.
      </p>
      <UForm
        ref="contactForm"
        class="bp-order__form"
        :state="state"
        :schema="schema"
        @submit="submitEmail"
      >
        <div class="bp-order__row">
          <UFormField
            name="name"
            :ui="fieldUi"
          >
            <label
              for="name"
              class="bp-field-label"
            >Name <span aria-hidden="true">*</span></label>
            <UInput
              id="name"
              v-model="state.name"
              variant="none"
              autocomplete="name"
              required
              class="w-full"
              :ui="inputUi"
            />
          </UFormField>
          <UFormField
            name="email"
            :ui="fieldUi"
          >
            <label
              for="email"
              class="bp-field-label"
            >Email address <span aria-hidden="true">*</span></label>
            <UInput
              id="email"
              v-model="state.email"
              type="email"
              variant="none"
              autocomplete="email"
              required
              class="w-full"
              :ui="inputUi"
            />
          </UFormField>
        </div>
        <UFormField
          name="message"
          :ui="fieldUi"
        >
          <label
            for="message"
            class="bp-field-label"
          >Message <span aria-hidden="true">*</span></label>
          <UTextarea
            id="message"
            v-model="state.message"
            variant="none"
            required
            class="w-full"
            :rows="5"
            autoresize
            :ui="inputUi"
          />
        </UFormField>
        <div class="bp-order__actions">
          <ClientOnly>
            <div>
              <altcha-widget
                ref="altchaWidget"
                challenge="/api/get_challenge"
                name="altcha_payload"
                hidelogo
                hidefooter
              />
            </div>
            <template #fallback>
              <span class="bp-altcha-slot" />
            </template>
          </ClientOnly>
          <button
            type="submit"
            class="bp-btn bp-btn--signal"
            :disabled="isSubmitting"
            :aria-busy="isSubmitting || undefined"
          >
            <span>{{ isSubmitting ? 'Sending' : 'Send message' }}</span>
            <BlueprintArrow />
          </button>
        </div>
      </UForm>
    </div>
  </section>
</template>

<script setup lang="ts">
// eslint-disable @typescript-eslint/no-explicit-any
import { z } from 'zod'
import type { FormSubmitEvent, Form } from '#ui/types'

const schema = z.object({
  email: z.email(),
  name: z.string().min(3).max(48),
  message: z.string().min(10).max(1024),
  contactChallengeToken: z.string()
})

type Schema = z.output<typeof schema>

const fieldUi = {
  error: 'bp-field-error'
}
const inputUi = { base: 'bp-input' }

const state = reactive({
  email: '',
  name: '',
  message: '',
  contactChallengeToken: ''
})

const contactForm = ref<Form<Schema> | undefined>(undefined)
const toast = useToast()
const altchaWidget = ref<HTMLElement | null>(null)
const isSubmitting = ref(false)

async function submitEmail(event: FormSubmitEvent<Schema>) {
  if (isSubmitting.value) return
  isSubmitting.value = true

  try {
    const contactBody = JSON.stringify({
      email: event.data.email,
      name: event.data.name,
      message: event.data.message,
      contactChallengeToken: (
        altchaWidget.value?.querySelector(
          'input[name=altcha_payload]'
        ) as HTMLInputElement
      )?.value
    })
    const response = (await $fetch('/api/contact/', {
      method: 'POST',
      body: contactBody,
      ignoreResponseError: true
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    })) as any

    if (response.statusCode === 422) {
      try {
        const errors = response.data.issues.map(
          (err: { message: string, path: string[] }) => ({
            message: err.message,
            path: Array.isArray(err.path) ? err.path.join('.') : err.path
          })
        )
        contactForm.value?.setErrors(errors)
      } catch (error: unknown) {
        const errorMessage
          = error instanceof Error ? error.message : 'Unknown error'
        toast.add({
          title: 'Validation Error',
          description: `An error occurred while validating your message. Please try again later. ${errorMessage}`,
          color: 'error'
        })
      }
    } else if (response.statusCode === 200) {
      toast.add({
        title: 'Message sent successfully!',
        color: 'success'
      })
      contactForm.value?.clear()
    } else {
      toast.add({
        title: 'Submission Error',
        description: `${response.statusCode}: ${response.error}`,
        color: 'error'
      })
      contactForm.value?.clear()
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>
